<template>
  <div class="submission-detail">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading submission...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <div class="error-icon">!</div>
      <h4>Error Loading Submission</h4>
      <p>{{ error }}</p>
    </div>

    <div v-else-if="submission" class="submission-content">
      <!-- Header Section -->
      <div class="submission-header-wrapper">
        <FeedItem
          :submission="submission"
          :showFooter="false"
          :clickable="false"
          :showAsCard="false"
        />
      </div>

      <!-- Tabbed Interface -->
      <div class="tabs-container">
        <div class="tabs-header">
          <button
            class="tab-button"
            :class="{ active: activeTab === 'comments' }"
            @click="activeTab = 'comments'"
          >
            Comments ({{ submission.comment_count || 0 }})
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'attachments' }"
            @click="activeTab = 'attachments'"
          >
            Attachments ({{ aggregatedAttachmentCount }})
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'data' }"
            @click="activeTab = 'data'"
          >
            Data ({{ aggregatedDataCount }})
          </button>
        </div>

        <div class="tabs-content">
          <CommentsList
            v-if="activeTab === 'comments'"
            :submissionId="submissionId"
          />
          <AttachmentsList
            v-if="activeTab === 'attachments'"
            :submissionId="submissionId"
          />
          <DataItemsList
            v-if="activeTab === 'data'"
            :submissionId="submissionId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import Submission from '@/api/Submission.js'
import FeedItem from '@/components/Feed/FeedItem.vue'
import CommentsList from './CommentsList.vue'
import AttachmentsList from './AttachmentsList.vue'
import DataItemsList from './DataItemsList.vue'

const props = defineProps({
  submissionId: {
    type: Number,
    required: true
  },
  activeTab: {
    type: String,
    default: 'comments',
    validator: (value) => ['comments', 'attachments', 'data'].includes(value)
  }
})

const submission = ref(null)
const loading = ref(true)
const error = ref(null)
const activeTab = ref(props.activeTab)
const aggregatedAttachmentCount = ref(0)
const aggregatedDataCount = ref(0)

onMounted(async () => {
  try {
    loading.value = true
    error.value = null

    // Fetch submission details
    const response = await Submission.getSubmission(props.submissionId)
    submission.value = response.data

    // Fetch aggregated counts in parallel
    const [attachmentCountResponse, dataCountResponse] = await Promise.all([
      Submission.getSubmissionAttachmentsAggregatedCount(props.submissionId),
      Submission.getSubmissionDataAggregatedCount(props.submissionId)
    ])

    aggregatedAttachmentCount.value = attachmentCountResponse.data.count
    aggregatedDataCount.value = dataCountResponse.data.count
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to load submission. Please try again.'
    console.error('Error loading submission:', err)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.submission-detail {
  padding: 2rem 1rem;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.submission-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6B7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(168, 85, 247, 0.2);
  border-top-color: #a855f7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  margin: 0;
  font-size: 0.95rem;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.error-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.error-state h4 {
  color: #FFFFFF;
  margin-bottom: 0.5rem;
}

.error-state p {
  color: #9CA3AF;
  margin: 0;
  font-size: 0.95rem;
}

/* Header Section */
.submission-header-wrapper {
  margin-bottom: 1.5rem;
}

/* Tabs Container */
.tabs-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.tabs-header {
  display: flex;
  gap: 0.5rem;
  padding: 0;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid rgba(255, 255, 255, 0.05);
}

.tab-button {
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.75rem 1rem;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}

.tab-button:hover {
  color: #9CA3AF;
}

.tab-button.active {
  color: #a855f7;
  border-bottom-color: #a855f7;
}

.tabs-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
